"use client";

import { ReactElement, useEffect, useState } from "react";
import FontAwesome, { FontAwesomeSize } from "../utilities/FontAwesome";
import Button from "../utilities/Button";
import styles from "../../pages/styles/MainGridPage.module.scss";
import followStyles from "./styles/FollowFeedPageItem.module.scss";
import { useStores } from "../../stores/useStores";
import { observer } from "mobx-react-lite";
import WatsonApi from "../../backends/WatsonApi";
import NotificationManager from "../notifications/NotificationManager";
import { TranslatedText, useT } from "../utilities/TranslatedText";

const FollowPageButton = observer(function FollowPageButton(props: {
  initialIsFollower: boolean;
  symbolOnly?: boolean;
  className?: string;
  smallIcon?: boolean;
  size?: FontAwesomeSize;
  organizationTitle: string;
  toggleFollowingOrganizationId: string;
}) {
  const { userStore, modalStore, organizationStore } = useStores();
  const t = useT();
  const [follower, setFollower] = useState(props.initialIsFollower);
  const [followIcon, setFollowIcon] = useState<ReactElement | null>(null);
  const [followText, setFollowText] = useState<ReactElement | null>(null);

  useEffect(() => {
    if (props.symbolOnly) {
      if (follower) {
        setFollowIcon(
          <FontAwesome
            prefix={"fas"}
            name={"fa-star"}
            size={props.smallIcon ? undefined : props.size}
          />,
        );
      } else {
        setFollowIcon(
          <FontAwesome
            name={"fa-star"}
            prefix={"far"}
            size={props.smallIcon ? undefined : props.size}
          />,
        );
      }
    } else {
      if (follower) {
        setFollowIcon(<FontAwesome prefix={"fas"} name={"fa-star"} />);
        setFollowText(
          <TranslatedText
            as={"div"}
            className={`${followStyles.hiddenWhenSmall}`}
            id="sb-follow-button-text"
            aria-label={t("Click to cancel your subscription")}
          >
            Subscribed
          </TranslatedText>,
        );
      } else {
        setFollowIcon(<FontAwesome name={"fa-star"} prefix={"far"} />);
        setFollowText(
          <TranslatedText
            as={"div"}
            className={`${followStyles.hiddenWhenSmall}`}
            id="sb-follow-button-text"
            aria-label={t("Click to subscribe")}
          >
            Subscribe
          </TranslatedText>,
        );
      }
    }
  }, [props.smallIcon, follower]);

  async function handleClick(e) {
    e.preventDefault();
    if (userStore.id) {
      try {
        const client = await WatsonApi();
        await client.apis.toggle_follow.toggle_follow_toggle_follow_organization(
          {
            itemname: props.toggleFollowingOrganizationId,
          },
        );
        const newState = !follower;
        setFollower((v) => !v);
        if (
          props.toggleFollowingOrganizationId ===
          organizationStore.currentOrganization.id
        ) {
          userStore.setPermissions({
            follower: newState,
          });
        }
        if (newState) {
          NotificationManager.success(
            "You are now subscribed to this organization.",
          );
        } else {
          NotificationManager.success(
            "You are no longer subscribed to this organization.",
          );
        }
      } catch (e) {
        NotificationManager.error(JSON.parse(e.response.text));
      }
    } else {
      modalStore.addModal({
        type: "login",
      });
    }
  }

  const analyticsProperties = {
    action: follower ? "unsubscribed" : "subscribed",
    target_organization_id: props.toggleFollowingOrganizationId,
    target_organization_title: props.organizationTitle,
  };
  const disabled = userStore.id && !userStore.viewer;
  const style = { cursor: disabled ? "not-allowed" : "pointer" };

  return (
    <>
      {props.symbolOnly ? (
        <Button
          disabled={disabled}
          style={style}
          onClick={handleClick}
          className={props.className}
          title={
            follower
              ? `${t("Unsubscribe from")} ${props.organizationTitle}`
              : `${t("Subscribe to")} ${props.organizationTitle}`
          }
          aria-label={
            follower
              ? `${t("Unsubscribe from")} ${props.organizationTitle}`
              : `${t("Subscribe to")} ${props.organizationTitle}`
          }
          analyticsEvent="subscription_toggled"
          analyticsProperties={analyticsProperties}
        >
          {followIcon}
        </Button>
      ) : (
        <Button
          onClick={handleClick}
          disabled={disabled}
          style={style}
          title={
            follower
              ? `${t("Unsubscribe from")} ${props.organizationTitle}`
              : `${t("Subscribe to")} ${props.organizationTitle}`
          }
          aria-label={
            follower
              ? `${t("Unsubscribe from")} ${props.organizationTitle}`
              : `${t("Subscribe to")} ${props.organizationTitle}`
          }
          className={`btn btn-default pull-right ${styles.classroomFollowFloatingButton}`}
          analyticsEvent="subscription_toggled"
          analyticsProperties={analyticsProperties}
        >
          {followIcon}
          {followText}
        </Button>
      )}
    </>
  );
});

export default FollowPageButton;
